import React, {useRef, useState} from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import Text from '../../../components/text/Text'
import UnderLineInput from '../../../components/input/UnderLineInput'
import WizButton from '../../../components/button/WizButton'
import WizSelectButton from '../../../components/button/WizSelectButton'
import WizImage from '../../../components/image/WizImage'
import {postUploadImage} from '../../../apis/image/postUploadImage'
import {createBadge} from '../../../apis/badge/createBadge'
import {BADGE_CATEGORIES, BADGE_TYPES} from '../../../apis/badge/getBadge'
import useNavigation from '../../../hooks/useNavigation'

interface IProps {
    className?: string
}

const categoryOptions = BADGE_CATEGORIES.map(c => ({id: c, value: c}))
const typeOptions = BADGE_TYPES.map(t => ({id: t, value: t}))

const BadgeCreatePage: React.FC<IProps> = ({className}) => {
    const {goBadge} = useNavigation()

    const [code, setCode] = useState('')
    const [category, setCategory] = useState<string>(BADGE_CATEGORIES[0])
    const [type, setType] = useState(BADGE_TYPES[0])
    const [nameEn, setNameEn] = useState('')
    const [nameKo, setNameKo] = useState('')
    const [descEn, setDescEn] = useState('')
    const [descKo, setDescKo] = useState('')
    const [criteriaEn, setCriteriaEn] = useState('')
    const [criteriaKo, setCriteriaKo] = useState('')
    const [requirementEn, setRequirementEn] = useState('')
    const [requirementKo, setRequirementKo] = useState('')
    const [image, setImage] = useState('')
    const [imageMini, setImageMini] = useState('')
    const [imageDisabled, setImageDisabled] = useState('')
    const [sortOrder, setSortOrder] = useState(0)
    const [remark, setRemark] = useState('')

    const imageRef = useRef<any>()
    const imageMiniRef = useRef<any>()
    const imageDisabledRef = useRef<any>()

    const onChangeImage = async (e: any, setFn: (url: string) => void) => {
        const file = e?.target?.files[0]
        if (file) {
            const result = await postUploadImage(file)
            if (result?.image_url) {
                setFn(result.image_url)
            }
        }
    }

    const onClickCreate = async () => {
        if (!code) {
            alert('Please enter Badge Code')
            return
        }
        if (!nameEn && !nameKo) {
            alert('Please enter Badge Name (at least one language)')
            return
        }

        const body = {
            code,
            category,
            type,
            grant_type: 'manual',
            name_i18n: {en: nameEn, ko: nameKo},
            description_i18n: {en: descEn, ko: descKo},
            criteria_i18n: {en: criteriaEn, ko: criteriaKo},
            requirement_i18n: {en: requirementEn, ko: requirementKo},
            image,
            image_mini: imageMini,
            image_disabled: imageDisabled,
            is_active: true,
            sort_order: sortOrder,
            remark: remark || undefined,
        }

        const {data, error} = await createBadge(body)

        if (data) {
            alert('Badge created successfully!')
            goBadge()
        } else {
            alert(`Badge creation failed: ${error || 'Unknown error'}`)
        }
    }

    return (
        <PageLayout title={'Create Badge'}>
            <div className={'max-w-[800px] mx-auto space-y-[20px]'}>
                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Badge Info</Text>

                    <div>
                        <Text className={'text-body3 text-font02'}>Code</Text>
                        <Text className={'text-caption text-font03'}>
                            Unique slug for frontend identification (e.g. asset_1m, wolf_pack). Lowercase + underscores only.
                        </Text>
                        <UnderLineInput
                            placeholder={'e.g. asset_1m'}
                            onChanged={text => setCode(text.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                        />
                    </div>

                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02 text-center'}>Category</Text>
                        <WizSelectButton
                            className={'mx-auto'}
                            data={categoryOptions}
                            onChange={e => setCategory(e.id)}
                            activeValue={categoryOptions.find(o => o.id === category)}
                        />
                    </div>

                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02 text-center'}>Type</Text>
                        <WizSelectButton
                            className={'mx-auto'}
                            data={typeOptions}
                            onChange={e => setType(e.id)}
                            activeValue={typeOptions.find(o => o.id === type)}
                        />
                    </div>

                    <div>
                        <Text className={'text-body3 text-font02'}>Sort Order</Text>
                        <Text className={'text-caption text-font03'}>
                            Badge list display order. Lower number = shown first.
                        </Text>
                        <UnderLineInput
                            placeholder={'0'}
                            onChanged={text => setSortOrder(Number(text))}
                        />
                    </div>

                    <div>
                        <Text className={'text-body3 text-font02'}>Remark (optional)</Text>
                        <Text className={'text-caption text-font03'}>
                            Internal note for admins. Not visible to users.
                        </Text>
                        <UnderLineInput
                            placeholder={'Internal note'}
                            onChanged={text => setRemark(text)}
                        />
                    </div>
                </div>

                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Localization</Text>

                    <div className={'grid grid-cols-2 gap-[20px] sm:grid-cols-1'}>
                        <div className={'space-y-[15px]'}>
                            <Text className={'text-ti2 text-center'}>English</Text>
                            <div>
                                <Text className={'text-body3 text-font02'}>Name (EN)</Text>
                                <UnderLineInput onChanged={text => setNameEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Description (EN)</Text>
                                <UnderLineInput onChanged={text => setDescEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Criteria (EN)</Text>
                                <UnderLineInput onChanged={text => setCriteriaEn(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Requirement (EN)</Text>
                                <UnderLineInput onChanged={text => setRequirementEn(text)} />
                            </div>
                        </div>
                        <div className={'space-y-[15px]'}>
                            <Text className={'text-ti2 text-center'}>Korean</Text>
                            <div>
                                <Text className={'text-body3 text-font02'}>Name (KO)</Text>
                                <UnderLineInput onChanged={text => setNameKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Description (KO)</Text>
                                <UnderLineInput onChanged={text => setDescKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Criteria (KO)</Text>
                                <UnderLineInput onChanged={text => setCriteriaKo(text)} />
                            </div>
                            <div>
                                <Text className={'text-body3 text-font02'}>Requirement (KO)</Text>
                                <UnderLineInput onChanged={text => setRequirementKo(text)} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'bg-white rounded-[10px] shadow-normal p-[30px] space-y-[25px]'}>
                    <Text className={'text-h3 text-center'}>Images</Text>

                    <div className={'grid grid-cols-3 gap-[20px] sm:grid-cols-1'}>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Image</Text>
                            <div
                                onClick={() => imageRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {image ? (
                                    <WizImage src={`${image}?type=webp&width=400`} className={'max-h-[140px] object-contain'} />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImage)}
                                ref={imageRef}
                                className={'hidden'}
                            />
                        </div>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Mini Image</Text>
                            <div
                                onClick={() => imageMiniRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {imageMini ? (
                                    <WizImage
                                        src={`${imageMini}?type=webp&width=200`}
                                        className={'max-h-[140px] object-contain'}
                                    />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImageMini)}
                                ref={imageMiniRef}
                                className={'hidden'}
                            />
                        </div>
                        <div>
                            <Text className={'text-body3 text-font02 mb-[10px]'}>Badge Disabled Image</Text>
                            <div
                                onClick={() => imageDisabledRef?.current?.click()}
                                className={
                                    'border border-line02 rounded-[10px] overflow-hidden cursor-pointer h-[150px] flex items-center justify-center bg-line04'
                                }>
                                {imageDisabled ? (
                                    <WizImage
                                        src={`${imageDisabled}?type=webp&width=400`}
                                        className={'max-h-[140px] object-contain'}
                                    />
                                ) : (
                                    <Text className={'text-font03'}>Click to upload</Text>
                                )}
                            </div>
                            <input
                                type={'file'}
                                accept={'image/*'}
                                onChange={e => onChangeImage(e, setImageDisabled)}
                                ref={imageDisabledRef}
                                className={'hidden'}
                            />
                        </div>
                    </div>
                </div>

                <div className={'text-center pb-[40px]'}>
                    <WizButton type={'primary'} size={'big'} text={'Create Badge'} onClick={onClickCreate} />
                </div>
            </div>
        </PageLayout>
    )
}

export default BadgeCreatePage
